/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { ImportScheduleInfo } from '../../../models/import-schedule-model';
import { ImportScheduleReturnCodes } from '../../../models/import-schedule-return-codes';
import { filter, Observable, Subscription } from 'rxjs';
import {
  IImportScheduleFacade,
  IMPORT_SCHEDULE_FACADE_TOKEN
} from '../../../../../../data-access/src/lib/domain/facades-models/import-schedule-facade';
import { GlobalConstants } from '../../../constants/global-constants';
import { AcrylicSurfaceService } from '../../../acrylic-surface/acrylic-surface.service';
import { AlertStatus, IAlert } from '../../../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'sysque-import-schedule',
  templateUrl: './import-schedule.component.html',
  styleUrls: ['./import-schedule.component.scss'],
  standalone: false
})
export class ImportScheduleComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter();
  public data: ImportScheduleInfo = {
    scheduleName: '',
    buttonName: ''
  }
  public componentTitle = '';
  public validFileName = '';
  public scheduleName = '';
  public isImportButtonDisabled = true;
  public warningMessage = '';
  public alertStatus: AlertStatus;
  public alertHeight = 342;
  public nonAlertHeight = 282;
  public isAlertVisible = false;

  private isScheduleNameValid = true;
  private selectedFileName= '';
  private selectedFileName$: Observable<string>;
  private selectedFileNameSub: Subscription;

  public get alert(): IAlert {
    return {
      type: this.alertStatus,
      message: this.warningMessage,
      maxHeight: '30px'
    };
  }

  constructor(@Inject(IMPORT_SCHEDULE_FACADE_TOKEN) private importScheduleFacade: IImportScheduleFacade, private acrylicSurfaceService: AcrylicSurfaceService) {}

  ngOnInit(): void {
    this.data.buttonName = GlobalConstants.ImportButton;
    this.data.scheduleName = GlobalConstants.ScheduleButton;
    this.componentTitle = GlobalConstants.IMPORT_SCHEDULE_NAME_DIALOG;
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));
    if (this.selectedFileNameSub) {
      this.selectedFileNameSub.unsubscribe();
    }
  }

  onBrowseClick() {
    this.selectedFileName$ = this.importScheduleFacade.browseImportSchedule().pipe(
      filter(response => response !== 'initial file name')
    );
    this.selectedFileNameSub = this.selectedFileName$.subscribe((response) => {
      if (response === ImportScheduleReturnCodes[ImportScheduleReturnCodes.NoFileSelected]) {
        return;
      }

      this.selectedFileName = response;
      const fileExtension = this.selectedFileName.split('.').pop();
      const allowedExtensions = ['.xls', '.xlsx'];

      if (!allowedExtensions.includes(`.${fileExtension}`)) {
        // Clear the selected file name
        this.validFileName = '';
        // Disable the Import button
        this.isImportButtonDisabled = true;
        // Show a toast notification with an error message
        this.showAlert('Please select a valid Excel file', AlertStatus.Warning);
      } else {
        this.validFileName = this.selectedFileName;
        this.validateImportButton();
      }
    });
  }

  onUserInputChanged() {
    this.validateImportButton();
  }

  validateImportButton() {
    this.isScheduleNameValid = /^[a-zA-Z]/.test(this.scheduleName);
    this.isImportButtonDisabled = !this.validFileName || !this.isScheduleNameValid;
    if (this.scheduleName.trim() === '' || this.isScheduleNameValid) {
      this.hideAlert();
    }
    if (!this.isScheduleNameValid && this.scheduleName.trim() !== '') {
      this.showAlert('Name must start with alphabetic character', AlertStatus.Info);
    }
  }

  showAlert(message: string, alertStatus: AlertStatus) {
    this.importScheduleFacade.windowResize(this.alertHeight);
    this.warningMessage = message;
    this.alertStatus = alertStatus;
    this.isAlertVisible = true;
  }

  hideAlert() {
    this.importScheduleFacade.windowResize(this.nonAlertHeight);
    this.warningMessage = '';
    this.isAlertVisible = false;
  }

  closeWindow() {
    this.close.emit();
    this.acrylicSurfaceService.hide();
  }

  importSchedule() {
    this.importScheduleFacade.importSchedule(this.scheduleName);
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === 'importScheduleResult') {
      if (event.newValue === '') {
        return;
      }
      const result = JSON.parse(event.newValue);
      if (result == ImportScheduleReturnCodes.Success) {
        const scheduleName = this.scheduleName;
        this.showAlert('<b>"' + scheduleName + '"</b> was imported successfully', AlertStatus.Success);
        this.validFileName = '';
        this.selectedFileName = '';
        this.scheduleName = '';
        this.isImportButtonDisabled = true;
      } else if (result == ImportScheduleReturnCodes.ExcelNotFound) {
        this.showAlert('Microsoft Excel application not found', AlertStatus.Error);
      } else if (result == ImportScheduleReturnCodes.DataNotFound) {
        this.showAlert('File does not contain importable data', AlertStatus.Warning);
      } else if (result == ImportScheduleReturnCodes.DuplicateName) {
        this.showAlert(
          '<b>"' + this.scheduleName + '"</b> is already in use, please input a unique unused name',
          AlertStatus.Warning
        );
      } else if (result == ImportScheduleReturnCodes.GeneralError) {
        this.showAlert('Something went wrong, please try again', AlertStatus.Warning);
      }
    }
  }
}
